import { useEffect, useContext, useRef } from 'react';

import { LightTooltip } from 'components/global/NodeLayout/NodeTopostman';
import Styles from './NodeProgress.module.scss';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';
import _ from 'lodash';

import axios from 'axios';
import { ViewContext, AuthContext, useCan } from 'components/lib';
import { decoder, encoder } from 'utils/helpers/decoder';

const NodeProgress = ({ sessionId, lab_id, node_id, setDrawerOpen,
    drawerOpen, currentStep, setCurrentStep, initialUserProgress, steps, data, maxUnlocked, setMaxUnlocked, resource, socket = null, onReveal, nodeInfo, modifiedCode }) => {

    const navigate = useNavigate();
    const viewContext = useContext(ViewContext);
    const isFirstStep = maxUnlocked ? maxUnlocked === 0 : currentStep === 0;

    const isSupervisor = useCan('node.progress.flag');
    const isAssessment = (nodeInfo?.is_assessment === 1);
    const timerRef = useRef(null);

    useEffect(() => {
        if (data) {
            if (maxUnlocked != null) {
                setMaxUnlocked(initialUserProgress)
            }

            initialUserProgress === steps.length ? setCurrentStep(0) : setCurrentStep(initialUserProgress);

            if (initialUserProgress > 0) {
                toast.dark(viewContext.t('Components.NodeLayout.NodeProgress.toast.resuming'), {
                    type: 'success',
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });

                let showNpmInstallMessage = false;

                for (let i = 0; i < initialUserProgress; i++) {
                    //TODO: Needs to be reviewed later. This is a temporary measure. If npm auto install is figured out then this can be removed
                    if (resource?.config?.showInstallMessage && !steps[i + 1].no_install) {
                        showNpmInstallMessage = true;
                    }
                }

                if (showNpmInstallMessage) {
                    toast.dark(viewContext.t('Components.NodeLayout.NodeProgress.toast.npmInstall'), {
                        type: 'info',
                        position: 'top-right',
                        autoClose: false,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                }
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    const resetProgress = () => {
        if (isFirstStep) {
            toast.dark(viewContext.t('Components.NodeLayout.NodeProgress.toast.first'), {
                type: 'info',
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            return;
        }

        const resetOptions = {
            method: 'PATCH',
            url: `/api/labs/${lab_id}/nodes/${node_id}/progress?reset=true`,
            headers: {
                accept: 'application/json',
            },
            data: {
                resource: resource,
                sessionId: sessionId,
            },
        };

        // Show loading toast and save its ID
        const toastId = toast.loading(viewContext.t('Components.NodeLayout.NodeProgress.toast.resetting'), {
            position: 'top-right',
            closeOnClick: true,
            draggable: true,
            theme: 'dark',
        });

        axios.request(resetOptions)
            .then(() => {
                toast.update(toastId, {
                    render: viewContext.t('Components.NodeLayout.NodeProgress.toast.reset'),
                    type: 'success',
                    isLoading: false,
                    autoClose: 3000,
                    hideProgressBar: false,
                    theme: 'dark',
                });
                setCurrentStep(0);
                if (maxUnlocked != null) {
                    setMaxUnlocked(0);
                }
                localStorage.removeItem('sessionId');
                window.location.reload();
            })
            .catch((err) => {
                toast.update(toastId, {
                    render: viewContext.t('Components.NodeLayout.NodeProgress.toast.resetError'),
                    type: 'error',
                    isLoading: false,
                    autoClose: 3000,
                    hideProgressBar: false,
                    theme: 'dark',
                });
                console.error("Reset failed:", err.message);
                navigate("/oops");
            });
    };

    const debounce = (func, delay) => {
        return (...args) => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                func.apply(null, args);
            }, delay);
        };
    };

    const handleDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };

    const saveProgress = () => {
        if (isFirstStep) {
            toast.dark(viewContext.t('Components.NodeLayout.NodeProgress.toast.proceed'), {
                type: 'info',
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            return;
        }

        const updateOptions = {
            method: 'PATCH',
            url: `/api/labs/${lab_id}/nodes/${node_id}/progress`,
            headers: {
                accept: 'application/json'
            },
            data: {
                userProgress: (maxUnlocked != null) ? maxUnlocked : currentStep,
                resource: resource,
                sessionId: sessionId,
            }
        };
        if (modifiedCode && nodeInfo?.resource?.config?.saveCode) {
            updateOptions.data.modifiedCode = encoder(modifiedCode ?? {})
        }

        const toastId = toast.loading(viewContext.t('Components.NodeLayout.NodeProgress.toast.saving'), {
            position: 'top-right',
            closeOnClick: true,
            draggable: true,
            theme: 'dark',
        });

        axios.request(updateOptions)
            .then((response) => {
                toast.update(toastId, {
                    render: viewContext.t('Components.NodeLayout.NodeProgress.toast.saved'),
                    type: 'success',
                    isLoading: false,
                    autoClose: 3000,
                    hideProgressBar: false,
                    theme: 'dark',
                });

                if (modifiedCode) modifiedCode = {};

                if (response?.data?.azCopyCommand && socket?.connected) {
                    const debouncedAzCopy = _.debounce(() => {
                        socket.emit('azcopy:run', decoder(response.data.azCopyCommand));
                    }, 3000);
                    debouncedAzCopy();
                }
            })
            .catch((err) => {
                toast.update(toastId, {
                    render: (
                        <div className={Styles.warnToast}>
                            {viewContext.t('Components.NodeLayout.NodeProgress.toast.saveError')}
                            <button onClick={saveProgress}>try again</button>
                        </div>),
                    type: 'warning',
                    isLoading: false,
                    autoClose: 3000,
                    hideProgressBar: false,
                    theme: 'dark',
                });
                console.error('Save progress error:', err.message);
            });
    };

    const debouncedSaveProgress = debounce(saveProgress, 5000);

    useEffect(() => {
        if (maxUnlocked && maxUnlocked > 0 && maxUnlocked !== initialUserProgress) {
            debouncedSaveProgress()
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [maxUnlocked]);

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <LightTooltip
                    title={viewContext.t('Components.NodeLayout.NodeProgress.tooltip.reset')}
                    arrow
                    placement="left"
                    classes={{
                        tooltip: {
                            backgroundColor: '#fff',
                            color: 'rgba(0, 0, 0, 0.87)',
                            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
                            fontSize: 11,
                        },
                    }}
                >
                    <button id="resetButton" className={Styles.resetBtn} onClick={resetProgress}>
                        <RestartAltIcon />
                    </button>
                </LightTooltip>

                {isSupervisor && isAssessment && (
                    <LightTooltip title="Reveal Answer" arrow placement="left">
                        <button className={Styles.referenceBtn} onClick={onReveal}>
                            <VisibilityIcon />
                        </button>
                    </LightTooltip>
                )}

                <LightTooltip
                    title={viewContext.t('Components.NodeLayout.NodeProgress.tooltip.reference')}
                    arrow
                    placement="left"
                    classes={{
                        tooltip: {
                            backgroundColor: '#fff',
                            color: 'rgba(0, 0, 0, 0.87)',
                            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
                            fontSize: 11,
                        },
                    }}
                >

                    {typeof drawerOpen !== 'undefined' && typeof setDrawerOpen === 'function' && (
                        <button id="referenceButton" className={Styles.referenceBtn} onClick={handleDrawer}>
                            <LightbulbIcon />
                        </button>
                    )}

                </LightTooltip>
            </div>
        </div>
    );
}

export default NodeProgress